/**
 * Shared class recipes for the app's data tables.
 *
 * Constants rather than wrapper components: the tables are plain `<table>`
 * markup on purpose — that is the design system's spreadsheet DNA — and
 * introducing `<Th>`/`<Td>` abstractions to carry three utility strings would
 * hide the structure without removing any decision. What was actually missing
 * was that the seven tables each answered "how does this behave on a phone"
 * slightly differently, so the answers live here and the markup stays readable.
 */

/**
 * The scrollable frame every table sits in.
 *
 * The `overflow-x-auto` was always here; what was missing is on `TABLE` below.
 */
export const TABLE_FRAME =
  'overflow-x-auto rounded-lg border border-default bg-surface'

/**
 * The table itself.
 *
 * `w-auto min-w-full` rather than `w-full`, which is the whole fix. A `w-full`
 * table shrinks to its container and wraps its cells instead of overflowing —
 * so `overflow-x-auto` never engages and "Adrianne K Hamilton Park" becomes
 * four lines tall. This sizes the table to its content: it stretches to fill
 * the frame when the columns are narrow, and overflows into a scroll when they
 * are not.
 *
 * A fixed `min-w-[42rem]` was the obvious alternative and is wrong: once the
 * lower-priority columns drop out below their breakpoints, the two remaining
 * columns stretch across the whole 42rem and a phone scrolls sideways to reach
 * a column that would have fitted on screen.
 *
 * `whitespace-nowrap` is set here rather than on every cell because
 * `white-space` inherits. A cell that genuinely should wrap opts out with
 * `whitespace-normal`.
 */
export const TABLE = 'w-auto min-w-full text-left text-sm whitespace-nowrap'

export const TH = 'px-3 py-2 font-medium'
export const TH_RIGHT = 'px-3 py-2 text-right font-medium'
export const TD = 'px-3 py-2'
export const TD_RIGHT = 'px-3 py-2 text-right'

/**
 * Column priority. Columns that are not `essential` drop out below a
 * breakpoint, so the horizontal scroll stays short enough to be worth doing
 * rather than a second page laid sideways.
 *
 * Apply the same constant to the `<th>` and its `<td>`s, or the columns shear.
 */
export const COL_SECONDARY = 'hidden md:table-cell'
export const COL_OPTIONAL = 'hidden lg:table-cell'

/**
 * The frozen first column: the name you scroll sideways to look things up by.
 *
 * `bg-inherit` rather than a fixed color so the cell keeps the row's
 * background — including on hover. A hardcoded `bg-surface` would leave one
 * pale cell stranded in an otherwise tinted row.
 */
export const COL_STICKY = 'sticky left-0 z-10 bg-inherit'

/**
 * Row background is explicit so `bg-inherit` above has something to inherit.
 * A transparent row would leave the sticky cell see-through, and the columns
 * it is meant to sit in front of would scroll visibly underneath it.
 */
export const ROW = 'bg-surface transition-colors hover:bg-subtle'

/**
 * A row action that appears on hover — Revoke, Archive, and friends.
 *
 * Visible by default and hidden only from `lg` up, which is the opposite of how
 * it reads. Progressive disclosure assumes a pointer that can hover somewhere
 * without committing to it; a touch screen has no such state, so an action
 * revealed on hover is an action a phone user cannot find at all. Below the
 * desktop breakpoint the actions simply show.
 *
 * `focus-within`, not `focus-visible`. The class is often on a wrapper around
 * the buttons, and a wrapper is not itself focusable. `:focus-visible` never
 * matches it, so the buttons stay at opacity 0 while keyboard focus is inside
 * them — opacity on the parent hides the children's focus rings too. Download
 * and Archive were reachable by Tab and invisible.
 */
export const ROW_ACTION =
  'lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100 lg:focus-within:opacity-100'
